import { CaretDownOutlined } from '@ant-design/icons';
import { Collapse, CollapseProps } from 'antd';
import { useCompanyContext } from 'contexts/CompanyContext';
import { Link } from 'react-router-dom';
import './FaqComponent.css';

function FaqComponent() {
  const { companyConfig } = useCompanyContext();
  const productItems: CollapseProps['items'] = [
    {
      label: `How does ${companyConfig.companyName} know when a user has passed?`,
      children: (
        <div>
          {companyConfig.companyName} uses an Administrator system to notify us when you have passed in order to "queue"
          up your messages. Your administrator can be anyone. Users typically rely on spouses, children or close family
          members.
        </div>
      )
    },
    {
      label: `How does ${companyConfig.companyName} know when to send my messages?`,
      children: (
        <div>
          During the message creation phase, users will be prompted to fill in the date they would like the message
          delivered. We understand however that the delivery date is often unknown, which is why we offer a variety of
          life events to help narrow what date information is required. For example, a "Final Message" requires no date
          information since the message will be delivered after your passing. On the other hand, if you wish to leave a
          "Birthday Message" to be delivered on your grandchild's 18th birthday, you would have the option to fill in
          the month, day and year that will occur.
        </div>
      )
    },
    {
      label: 'What is Life Review?',
      children: (
        <div>
          Life Review is a guided storytelling experience that helps you reflect on your life, memories, values,
          relationships, and the moments that shaped you. Instead of starting with a blank page, Eternal Applications
          provides thoughtful prompts to help you tell your story in your own words.
          <br />
          <br />
          Life Review can be used to preserve memories for loved ones, reflect personally, support emotional and
          spiritual care, or create a meaningful record of your life experiences.
        </div>
      )
    },
    {
      label: 'How does Life Review work?',
      children: (
        <div>
          You will be guided through a series of Life Review questions and reflection prompts. You can answer the
          questions at your own pace, skip any prompts you do not wish to answer, and return later to continue or make
          edits.
          <br />
          <br />
          Depending on the features available to you, you may be able to type your responses or record audio responses
          directly on the platform.
        </div>
      )
    },
    {
      label: 'Do I have to complete the entire Life Review at once?',
      children: (
        <div>
          No. Life Review is designed to be flexible. You can complete as much or as little as you would like, save
          your progress, and return later. Your story does not need to be finished in one sitting.
        </div>
      )
    },
    {
      label: 'Can I edit my Life Review responses?',
      children: (
        <div>
          Yes. You can review, update, revise, or delete your Life Review responses at any time. We understand that
          memories, reflections, and the way you want to tell your story may change over time.
        </div>
      )
    },
    {
      label: 'Who can see my Life Review?',
      children: (
        <div>
          Your Life Review is private unless you choose to share it or your organization is helping you create it as
          part of your care or support experience. Eternal Applications is designed to protect your information and only
          make it available according to the permissions, sharing choices, and privacy terms that apply to your account.
        </div>
      )
    },
    {
      label: 'Can someone from my care team or organization help me complete Life Review?',
      children: (
        <div>
          Yes, if you are using Eternal Applications through a participating organization, an authorized team member may
          be able to help you create, record, or complete your Life Review. This can be helpful if you prefer to speak
          your answers aloud, need assistance using the platform, or want guided support during the process.
        </div>
      )
    },
    {
      label: 'Is Life Review the same thing as creating a message?',
      children: (
        <div>
          No. Life Review is focused on capturing your broader life story, memories, values, and reflections. Messages
          are typically created for a specific recipient and may be scheduled for delivery after your passing or on a
          future life event, such as a birthday, anniversary, or holiday.
          <br />
          <br />
          Both features are designed to help preserve connection, but they serve different purposes.
        </div>
      )
    },
    {
      label: 'Can my Life Review be shared with loved ones?',
      children: (
        <div>
          Yes, depending on the sharing options available to you. Life Review is designed to help preserve your story so
          it can be shared with the people who matter most, either now or in the future. You remain in control of what
          you choose to complete and share.
        </div>
      )
    },
    {
      label: 'What if I want to cancel a message or make edits?',
      children: (
        <div>
          The platform was specifically designed to be as user-friendly and as flexible as possible. We understand that
          your reflections change and relationships develop. At any point, and as often as you desire, you can edit your
          message, change the date of receipt, the recipient, the administrator, or even delete the whole thing and
          start from scratch.
        </div>
      )
    },
    {
      label: 'How does delivery of my message work?',
      children: (
        <div>
          In creating your message you will designate the recipient of the message and their contact information. When
          the date of delivery arrives, {companyConfig.companyName} will send the recipient a link to a secure page
          where the user will be able to read, view or listen to your message. They will also have the opportunity to
          download the message and save it to their phone, computer or tablet, keeping your message close to heart
          forever.
        </div>
      )
    },
    {
      label: 'Are there any hidden fees or storage costs?',
      children: (
        <div>
          Absolutely not. There are no hidden fees or additional charges of any sort. {companyConfig.companyName}{' '}
          strives to make the purchasing process as quick and painless as possible.
        </div>
      )
    }
  ];

  const securityItems: CollapseProps['items'] = [
    {
      label: 'How is my data protected?',
      children: (
        <div>
          {companyConfig.companyName} takes data protection and privacy very seriously. We employ a comprehensive
          approach to safeguard your data, ensuring it remains confidential and secure. Here are some key measures we
          have in place to protect your data: (1) Data Encryption: We use advanced encryption techniques to protect data
          both in transit and at rest. This means that any information transferred between your device and our servers,
          as well as the data stored on our servers, is encrypted to prevent unauthorized access. (2) Data Minimization:
          We collect and retain only the data that is necessary for the intended purpose. Unnecessary data is promptly
          deleted, reducing the risk of data exposure. (3) Privacy Policies: We have clear and transparent privacy
          policies in place that explain how we collect, use, and protect your data. These policies are easily
          accessible for your reference.
        </div>
      )
    },
    {
      label: `Does ${companyConfig.companyName} sell any of my data?`,
      children: (
        <div>
          No, we do not engage in selling any of your personal data. Your information is kept confidential and used only
          for the purposes outlined in our privacy policy, which prioritizes safeguarding your data integrity. We are
          committed to maintaining trust and transparency with our users, ensuring that your information remains
          protected at all times.
        </div>
      )
    },
    {
      label: 'Who will have access to view my message?',
      children: (
        <div>
          No one will have access to your messages other than yourself and the recipient of your message. Terms and
          Conditions apply.
        </div>
      )
    },
    {
      label: 'How can I delete my account and all associated data from the platform?',
      children: (
        <div>
          To request the deletion of your account and all associated data, please contact our support team by sending an
          email to <a href={`mailto:${companyConfig.contactEmail}`}>{companyConfig.contactEmail}</a> with the subject
          line "Account Deletion Request." Make sure to provide the email address associated with your account for
          verification purposes.
        </div>
      )
    },
    {
      label: 'Is my credit card information secure?',
      children: (
        <div>
          {companyConfig.companyName} partners with Stripe to offer a comprehensive and secure payment solution. Stripe
          is a trusted partnered to many major companies and institutions such as Google, Amazon, Lyft and More!
        </div>
      )
    }
  ];

  const legalItems: CollapseProps['items'] = [
    {
      label: 'Where can I find your terms and conditions?',
      children: (
        <div>
          You can find our terms and conditions in the footer of our website. Additionally, you can access them directly
          via the following links:{' '}
          <Link to={companyConfig.privacyUrl} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>{' '}
          OR{' '}
          <Link to={companyConfig.termsUrl} target="_blank" rel="noopener noreferrer">
            Terms of Service
          </Link>
          . These documents outline the terms of use and our commitment to protecting your privacy and data security. If
          you have any questions or concerns regarding our terms and conditions, please don't hesitate to contact us.
        </div>
      )
    },
    {
      label: 'Is my message legally binding?',
      children: (
        <div>
          Messages are not legally binding and do not supersede any legal document of trust or inheritance of any kind
          unless specifically detailed by your Estate Planning attorney. Users may use the platform to convey last
          wishes, understanding they hold no legal grounds. All messages must also comply with all applicable laws and
          regulations of your given country/state/region, etc.
        </div>
      )
    },
    {
      label: 'What type of content is not allowed on the site?',
      children: (
        <div>
          At {companyConfig.companyName}, we strive to maintain a safe and respectful online environment for all users.
          Therefore, we prohibit certain types of content that may be harmful, offensive, or violate our community
          guidelines. Content that is not allowed on our site includes, but is not limited to:
          <ol>
            <li>Illegal or unlawful activities.</li>
            <li>
              Content that promotes hate speech, discrimination, or violence against individuals or groups based on
              race, ethnicity, religion, gender, sexual orientation, disability, or any other characteristic.
            </li>
            <li>Any form of harassment, bullying, or intimidation.</li>
            <li>Inappropriate or explicit content, including nudity, pornography, or sexually explicit material.</li>
            <li>
              Content that infringes upon the intellectual property rights of others, including copyright or trademark
              violations.
            </li>
            <li>
              Spam, scams, or deceptive content.Any material that may pose a security risk or harm to our users or the
              integrity of our platform.
            </li>
          </ol>
          We reserve the right to remove any content that violates these guidelines and take appropriate action,
          including suspension or termination of user accounts. Our priority is to foster a positive and respectful
          online community where users can engage safely and responsibly. If you come across any content that you
          believe violates our guidelines, please report it to{' '}
          <a href={`mailto:${companyConfig.contactEmail}`}>{companyConfig.contactEmail}</a> for review.
        </div>
      )
    }
  ];

  return (
    <div className="faq-component">
      <h3>Product</h3>
      <Collapse
        expandIcon={({ isActive }) => <CaretDownOutlined rotate={isActive ? 180 : 0} />}
        expandIconPosition="end"
        items={productItems}
      />

      <h3>Security and Privacy</h3>
      <Collapse
        expandIcon={({ isActive }) => <CaretDownOutlined rotate={isActive ? 180 : 0} />}
        expandIconPosition="end"
        items={securityItems}
      />

      <h3>Legal</h3>
      <Collapse
        expandIcon={({ isActive }) => <CaretDownOutlined rotate={isActive ? 180 : 0} />}
        expandIconPosition="end"
        items={legalItems}
      />
    </div>
  );
}

export default FaqComponent;
